import normalize from 'json-api-normalizer';
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { BidsWithOuter, JSONApiDataObject } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { BidListStatuses, TabIds } from 'Douleutaras/views/seller-dashboard/jobs-list/types';
import { jobBidsBookNowSlice } from 'Douleutaras/redux/data/jobBids/jobBidsBookNowSlice';
import { jobsSlice } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { jobMunicipalitySlice } from 'Douleutaras/redux/data/jobMunicipality/jobMunicipalitySlice';
import { categoriesSlice } from 'Douleutaras/redux/data/categories/categoriesSlice';
import jobPriceDefiningAnswersSlice from 'Douleutaras/redux/data/jobPriceDefiningAnswers/jobPriceDefiningAnswersSlice';
import jobPriceDefiningQuestionsSlice from 'Douleutaras/redux/data/jobPriceDefiningQuestions/jobPriceDefiningQuestionsSlice';
import { jobBidsSlice } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { outerJobSlice } from 'Douleutaras/redux/data/jobs/outerJobSlice';
import { noteSlice } from 'Douleutaras/redux/data/notes/noteSlice';
import { mapTabAttribute } from 'Douleutaras/views/seller-dashboard/jobs-list/constants/jobTabsConfiguration';
import { deepMergeMetaData, infinitePaginationMergeMeta } from 'Douleutaras/utilities/redux/merge';

const jobBidsWithOuterAdapter = createEntityAdapter<JSONApiDataObject<BidsWithOuter>>({
  selectId: item => item.id
});

export const API_BID_WITH_OUTER_ENDPOINT = 'api/public/v3/bid-with-outer/';

function createEndpointIdentifier(status: BidListStatuses, tabSelection: TabIds): string {
  return `${API_BID_WITH_OUTER_ENDPOINT}${status}/${tabSelection}`;
}

interface FetchBidsArgs {
  status: BidListStatuses;
  page?: number;
  params?: {};
  filterJobStatuses?: Array<string>;
  filterJobIntent?: Array<string>;
  filterJobCategory?: Array<string>;
  filterJobMunicipality?: Array<string>;
  tabSelection: TabIds;
}

// Transforms combination of Status into meaningful params to get from the API,
// that's because we don't have a single way to get each status properly, and we need combination of params.
//
// CONCLUDED tabs sort by `-bn_modified` (BookNowEntry.modified) as the closest available
// proxy for completion date: the `bid-with-outer` endpoint's ordering_fields only expose
// `bid_created`/`bn_modified` server-side, no dedicated completion-date field. Concluding
// a job is normally the last write to its book-now entry, so `modified` tracks completion
// in practice.
const mapTabToBidEndpointParams = (status: BidListStatuses, tabSelection?: TabIds) => {
  switch (true) {
    default:
    case status === BidListStatuses.CURRENT:
      return {
        sort: 'bn_modified',
        'filter[tab]': [TabIds.NEW, TabIds.TODAY, TabIds.NEXT, TabIds.LATER].indexOf(tabSelection) !== -1
          ? tabSelection
          : TabIds.ALL
      };
    case status === BidListStatuses.CONCLUDED && tabSelection === TabIds.BY_YOU:
      return {
        'filter[tab]': 'concluded',
        'filter[concluded_by_pro]': '1',
        sort: '-bn_modified'
      };
    case status === BidListStatuses.CONCLUDED && tabSelection === TabIds.BY_ANOTHER_PROFESSIONAL:
      return {
        'filter[tab]': 'concluded_by_another_pro',
        sort: '-bn_modified'
      };
    case status === BidListStatuses.CONCLUDED && tabSelection === TabIds.OUTSIDE_DOULEUTARAS:
      return {
        'filter[tab]': 'concluded',
        'filter[job__cancel_reason]': 'seller_not_douleutaras',
        sort: '-bn_modified'
      };
    case status === BidListStatuses.CONCLUDED:
      return {
        'filter[tab]': 'concluded',
        sort: '-bn_modified'
      };
    case status === BidListStatuses.CANCELLED && tabSelection === TabIds.BY_YOU:
      return {
        'filter[tab]': 'cancelled',
        'filter[cancelled_by_pro]': '1'
      };
    case status === BidListStatuses.CANCELLED && tabSelection === TabIds.BY_THE_CUSTOMER:
      return {
        'filter[tab]': 'cancelled',
        'filter[cancelled_by_pro]': '0'
      };
    case status === BidListStatuses.CANCELLED:
      return {
        'filter[tab]': 'cancelled'
      };
  }
};

export const bidOuterHandleResponse = (response, api, endpoint) => {
  const bidOuterNormalized = normalize(response.data, { endpoint });
  const { jobBidsAdded } = jobBidsSlice.actions;
  const { jobBidsBookNowAdded } = jobBidsBookNowSlice.actions;
  const { jobsAdded } = jobsSlice.actions;
  const { jobMunicipalityAdded } = jobMunicipalitySlice.actions;
  const { outerJobsAdded } = outerJobSlice.actions;
  const { categoriesAdded } = categoriesSlice.actions;
  const { jobPriceDefiningAnswersAdded } = jobPriceDefiningAnswersSlice.actions;
  const { jobPriceDefiningQuestionAdded } = jobPriceDefiningQuestionsSlice.actions;
  const { noteAdded } = noteSlice.actions;

  if (bidOuterNormalized.outerJob) {
    api.dispatch(outerJobsAdded(bidOuterNormalized.outerJob));
  }

  if (bidOuterNormalized.bids) {
    api.dispatch(jobBidsAdded(bidOuterNormalized.bids));
  }

  if (bidOuterNormalized.bookNowEntries) {
    api.dispatch(jobBidsBookNowAdded(bidOuterNormalized.bookNowEntries));
  }

  if (bidOuterNormalized.jobs) {
    api.dispatch(jobsAdded(bidOuterNormalized.jobs));
  }

  if (bidOuterNormalized.municipality) {
    api.dispatch(jobMunicipalityAdded(bidOuterNormalized.municipality));
  }

  if (bidOuterNormalized.categories) {
    api.dispatch(categoriesAdded(bidOuterNormalized.categories));
  }

  if (bidOuterNormalized.answers) {
    api.dispatch(jobPriceDefiningAnswersAdded(bidOuterNormalized.answers));
  }

  if (bidOuterNormalized.questions) {
    api.dispatch(jobPriceDefiningQuestionAdded(bidOuterNormalized.questions));
  }

  if (bidOuterNormalized.note) {
    api.dispatch(noteAdded(bidOuterNormalized.note));
  }

  bidOuterNormalized.endpoint = endpoint;

  return bidOuterNormalized;
};

export const fetchBidsWithOuter = createAsyncThunk(
  'bidsWithOuter/get',
  async ({ status, page, params = {}, filterJobStatuses = [], filterJobIntent, filterJobCategory, filterJobMunicipality, tabSelection }
           : FetchBidsArgs = undefined,
         api) => {
    const endpointIdentifier = createEndpointIdentifier(status, tabSelection);
    const bidEndpointParams = mapTabToBidEndpointParams(status, tabSelection);
    // // Filters
    const jobIntentFilter = { 'filter[intention]': filterJobIntent };
    const jobCategoryFilter = { 'filter[job__category]': filterJobCategory };
    const jobMunicipalityFilter = { 'filter[job__municipality]': filterJobMunicipality };
    const jobStatusFilter = filterJobStatuses.length > 0 ? {
      'filter[book_now_entry__status]': filterJobStatuses.join(',')
    } : {};

    const response = await makeRequest(
      API_BID_WITH_OUTER_ENDPOINT,
      {
        method: 'GET',
        additionalGetParams: {
          ...{
            'page[number]': page
          },
          ...params,
          ...bidEndpointParams,
          ...jobStatusFilter,
          ...jobCategoryFilter,
          ...jobMunicipalityFilter,
          ...jobIntentFilter
        }
      }
    )(api);

    return bidOuterHandleResponse(response, api, endpointIdentifier);
  });

export const jobBidsWithOuterSlice = createSlice({
  name: 'jobBidsWithOuter',
  initialState: jobBidsWithOuterAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  }),
  reducers: {
    bidsRemoved: (state, action) => {
      return jobBidsWithOuterAdapter.removeMany(state, action.payload);
    },
    updateMetaAfterStatusChanged: (state, action) => {
      Object.keys(state.meta)
        .forEach(metaKey => state.meta[metaKey].data = state.meta[metaKey].data.filter(bid => bid.id !== action.payload.id));
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchBidsWithOuter.fulfilled, (state, action) => {
      if (action.payload.bidWithOuter) {
        jobBidsWithOuterAdapter.setMany(state, action.payload.bidWithOuter);
      }
      infinitePaginationMergeMeta(action, state);
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchBidsWithOuter.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(fetchBidsWithOuter.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
  }
});

// Select bids list based on tab and page number
export const selectBidsWithOuterMetaByTab = createSelector([
  state => state.jobBidsWithOuter,
  (state, { tab, status }) => ({ tab, status })
], (bidsWithOuter, { tab, status }) => {

  const metaKey = `${API_BID_WITH_OUTER_ENDPOINT}${status}/${tab}`;
  // Current tab doesn't need different key, it loads one list
  // Logic of tabs go here...
  const metaBidsData = bidsWithOuter.meta[metaKey]?.data;
  const entities = metaBidsData?.map(metaBidData => {
    return bidsWithOuter.entities[metaBidData.id];
  });

  // Return all bids only on switching tabs which are not in "Current" status
  // Current status works in a different way, they don't fetch new bids but use existing instead
  if (tab === TabIds.ALL) {

    return entities;
  }

  return entities?.filter(bidWithOuter => {
    return mapTabAttribute(bidWithOuter.attributes.tab) === tab;
  });
});

export const jobBidsWithOuterSelectors = jobBidsWithOuterAdapter.getSelectors((state: RootState) => state.jobBidsWithOuter);
export const { updateMetaAfterStatusChanged } = jobBidsWithOuterSlice.actions;
